import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Droplets,
  Gauge,
  Home,
  Landmark,
  Lightbulb,
  Loader2,
  Mail,
  MessageCircle,
  Package,
  Phone,
  Search,
  Send,
  ShieldCheck,
  Sprout,
  SunMedium,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { LOGO } from "./Navbar";
import { DEFAULT_QUOTE, loadQuote, submitQuotation } from "../Libs/quoteData";

/* -------------------------------------------------------------------------- */
/*  HOW TO USE                                                                */
/*                                                                            */
/*  <QuoteProvider> wraps the app (see App.js). Any component can then do:    */
/*                                                                            */
/*    const { openQuote } = useQuote();                                       */
/*    <button onClick={() => openQuote()}>Get a free quote</button>           */
/*                                                                            */
/*  Optionally pre-select services (e.g. from a shop product):                */
/*    openQuote({ services: ["Pumps, panels and tanks"], notes: "..." })      */
/*                                                                            */
/*  The wording and choices come from Firestore (site/quote), loaded once     */
/*  when the app starts. If that fails, DEFAULT_QUOTE from quoteData.js is    */
/*  used. Finished requests are saved to orders/quotations/requests.          */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  CONTENT                                                                   */
/* -------------------------------------------------------------------------- */

const DB_TIMEOUT_MS = 6000;

// The manager stores icons as keys. Unknown keys fall back to a package.
const ICONS = {
  droplets: Droplets,
  search: Search,
  gauge: Gauge,
  sun: SunMedium,
  zap: Zap,
  wrench: Wrench,
  package: Package,
  bulb: Lightbulb,
  home: Home,
  sprout: Sprout,
  building: Building2,
  landmark: Landmark,
};

// These stay in code on purpose: the form logic depends on them.
const METHODS = [
  { id: "call", label: "Phone call", icon: Phone },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "Email", icon: Mail },
];

const COUNTIES = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa",
  "Homa Bay", "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi",
  "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu",
  "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa",
  "Murang'a", "Nairobi", "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
  "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River", "Tharaka-Nithi",
  "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot",
];

// The first three are the main markets, so they come first in the list.
const COUNTRIES = [
  "Kenya",
  "Tanzania",
  "Uganda",
  "Rwanda",
  "Burundi",
  "South Sudan",
  "Ethiopia",
  "Somalia",
  "DR Congo",
  "Djibouti",
  "Eritrea",
];

const EMPTY = {
  services: [],
  country: "Kenya",
  // A county for Kenya, a region or district for every other country.
  county: "",
  area: "",
  property: "",
  timeline: "",
  notes: "",
  name: "",
  phone: "",
  email: "",
  method: "call",
};

/* -------------------------------------------------------------------------- */
/*  CONTENT LOADING                                                           */
/* -------------------------------------------------------------------------- */

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);

const str = (v, fallback = "") => (typeof v === "string" && v.trim() ? v : fallback);

// Cleans the document so a half-filled or older one can never break the form:
// every field has a safe value and icons become components.
function normalize(data) {
  const d = DEFAULT_QUOTE;

  const steps =
    Array.isArray(data?.steps) && data.steps.length === 3
      ? data.steps.map((s, i) => ({
          label: str(s?.label, d.steps[i].label),
          short: str(s?.short, d.steps[i].short),
          title: str(s?.title, d.steps[i].title),
          sub: str(s?.sub, d.steps[i].sub),
        }))
      : d.steps;

  const choices = (items, fallback, withHint) => {
    const clean = (Array.isArray(items) ? items : [])
      .filter((x) => str(x?.label))
      .map((x) => ({
        label: x.label.trim(),
        hint: withHint ? str(x.hint) : "",
        Icon: ICONS[x.icon] || Package,
      }));
    return clean.length ? clean : choices(fallback, fallback, withHint);
  };

  const timeline = (Array.isArray(data?.timeline) ? data.timeline : []).filter(
    (t) => typeof t === "string" && t.trim()
  );

  return {
    panel: {
      title: str(data?.panel?.title, d.panel.title),
      text: str(data?.panel?.text, d.panel.text),
      note: str(data?.panel?.note, d.panel.note),
    },
    steps,
    services: choices(data?.services, d.services, true),
    property: choices(data?.property, d.property, false),
    timeline: timeline.length ? timeline : d.timeline,
    success: {
      title: str(data?.success?.title, d.success.title),
      text: str(data?.success?.text, d.success.text),
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  VALIDATION                                                                */
/* -------------------------------------------------------------------------- */

function validate(step, f) {
  const e = {};
  if (step === 0 && f.services.length === 0) {
    e.services = "Pick at least one option so we know what to quote.";
  }
  if (step === 1) {
    if (!f.country) {
      e.country = "Choose the country where the work will happen.";
    } else if (f.country === "Kenya") {
      if (!f.county) e.county = "Choose the county where the work will happen.";
    } else {
      if (!f.county.trim()) e.county = "Enter your region or district.";
      if (!f.area.trim()) e.area = "Enter your town or village.";
    }
    if (!f.property) e.property = "Tell us what kind of property this is.";
    if (!f.timeline) e.timeline = "Let us know roughly when you want to start.";
  }
  if (step === 2) {
    if (!f.name.trim()) e.name = "Enter your name.";
    if (f.phone.replace(/\D/g, "").length < 9) {
      e.phone = "Enter a phone number we can reach you on.";
    }
    if (f.email.trim() && !/^\S+@\S+\.\S+$/.test(f.email.trim())) {
      e.email = "That email address does not look right.";
    }
    if (f.method === "email" && !f.email.trim()) {
      e.email = "Add your email so we can send the quote there.";
    }
  }
  return e;
}

/* -------------------------------------------------------------------------- */
/*  SMALL UI PIECES                                                           */
/* -------------------------------------------------------------------------- */

const inputClass = (err) =>
  `w-full rounded-xl border bg-white px-4 py-3.5 text-[#0E1726] outline-none transition focus:ring-2 ${
    err
      ? "border-red-400 focus:border-red-500 focus:ring-red-200"
      : "border-slate-300 focus:border-[#1F7BE0] focus:ring-[#1F7BE0]/25"
  }`;

function ErrorText({ children }) {
  if (!children) return null;
  return (
    <p data-error="true" role="alert" className="mt-2 text-sm font-medium text-red-600">
      {children}
    </p>
  );
}

function Field({ label, optional, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between text-sm font-semibold text-slate-700">
        {label}
        {optional && <span className="font-normal text-slate-400">Optional</span>}
      </span>
      {children}
      <ErrorText>{error}</ErrorText>
    </label>
  );
}

function Group({ legend, error, children }) {
  return (
    <fieldset>
      <legend className="mb-2.5 text-sm font-semibold text-slate-700">{legend}</legend>
      {children}
      <ErrorText>{error}</ErrorText>
    </fieldset>
  );
}

function ChoiceBase({ selected, value, onPick, icon: Icon, label, hint, multi = false, compact = false }) {
  return (
    <button
      type="button"
      role={multi ? "checkbox" : "radio"}
      aria-checked={selected}
      onClick={() => onPick(value)}
      className={`group relative flex w-full rounded-2xl border-2 p-4 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F7BE0] ${
        compact ? "flex-col items-start gap-3" : "items-center gap-4"
      } ${
        selected
          ? "border-[#0A3568] bg-[#0A3568]/[0.04]"
          : "border-slate-200 bg-white hover:border-[#1F7BE0]/50 hover:bg-[#F3F7FB]"
      }`}
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-150 ${
          selected
            ? "bg-[#0A3568] text-white"
            : "bg-[#F3F7FB] text-[#1F7BE0] group-hover:bg-white"
        }`}
      >
        <Icon size={22} />
      </span>
      <span className="min-w-0 flex-1 pr-5">
        <span className="block font-semibold leading-snug text-[#0E1726]">{label}</span>
        {hint && <span className="mt-0.5 block text-sm text-slate-500">{hint}</span>}
      </span>
      <span
        aria-hidden="true"
        className={`absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full transition duration-150 ${
          selected ? "scale-100 bg-[#0A3568] text-white" : "scale-75 opacity-0"
        }`}
      >
        {selected && <Check size={12} strokeWidth={3} />}
      </span>
    </button>
  );
}

function ChipBase({ selected, value, onPick, icon: Icon, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onPick(value)}
      className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-semibold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F7BE0] ${
        selected
          ? "border-[#0A3568] bg-[#0A3568] text-white"
          : "border-slate-200 bg-white text-slate-700 hover:border-[#1F7BE0]/50 hover:bg-[#F3F7FB]"
      }`}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  MODAL                                                                     */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  SEARCHABLE LIST  (type to filter, arrow keys + Enter, or tap an option)   */
/*  Used for the country and the Kenyan county, so nobody has to scroll.      */
/* -------------------------------------------------------------------------- */

// "Muranga" finds "Murang'a", "uasin" finds "Uasin Gishu", and so on.
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

function Combobox({ id, label, value, onChange, options, placeholder, error }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);

  // Matches that start with what was typed come first, then the rest.
  const results = useMemo(() => {
    const q = norm(query);
    if (!q) return options;
    const starts = [];
    const has = [];
    options.forEach((o) => {
      const n = norm(o);
      if (n.startsWith(q)) starts.push(o);
      else if (n.includes(q)) has.push(o);
    });
    return [...starts, ...has];
  }, [query, options]);

  useEffect(() => {
    if (open) {
      document
        .getElementById(`${id}-opt-${hi}`)
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [hi, open, id]);

  const commit = (option) => {
    onChange(option);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHi(Math.max(0, options.indexOf(value)));
      } else {
        setHi((h) => Math.min(h + 1, results.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open) {
      // keep Enter from submitting the form while the list is open
      e.preventDefault();
      if (results[hi]) commit(results[hi]);
    } else if (e.key === "Escape" && open) {
      // close the list only, not the whole modal
      e.stopPropagation();
      setOpen(false);
      setQuery("");
    }
  };

  const onBlur = () => {
    // Tabbing away with a single match typed picks it.
    if (query && results.length === 1) onChange(results[0]);
    setOpen(false);
    setQuery("");
  };

  const listId = `${id}-list`;

  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-semibold text-slate-700"
      >
        {label}
      </label>
      <div className="relative">
        <Search
          size={18}
          aria-hidden="true"
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && results[hi] ? `${id}-opt-${hi}` : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={open ? query : value}
          placeholder={open && value ? value : placeholder}
          onFocus={(e) => {
            setOpen(true);
            setQuery("");
            setHi(Math.max(0, options.indexOf(value)));
            e.target.select();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHi(0);
          }}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          className={`${inputClass(error)} pl-11 pr-11`}
        />
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-transform duration-150 ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute z-30 mt-1.5 max-h-60 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
        >
          {results.length === 0 ? (
            <li className="px-4 py-3 text-sm text-slate-500">
              No match. Check the spelling.
            </li>
          ) : (
            results.map((o, i) => (
              <li
                key={o}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={o === value}
                // keep focus in the input so the list does not close before the click lands
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(o)}
                onMouseEnter={() => setHi(i)}
                className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 ${
                  i === hi ? "bg-[#F3F7FB]" : ""
                } ${o === value ? "font-semibold text-[#0A3568]" : "text-[#0E1726]"}`}
              >
                {o}
                {o === value && <Check size={16} className="shrink-0 text-[#1F7BE0]" />}
              </li>
            ))
          )}
        </ul>
      )}
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

// memo() means typing in a field no longer re-renders every card and chip
const Choice = memo(ChoiceBase);
const Chip = memo(ChipBase);

function QuoteModal({ content, preset, onClose }) {
  const reduce = useReducedMotion();
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);

  const STEPS = content.steps;

  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    services: (preset?.services || []).filter((s) =>
      content.services.some((x) => x.label === s)
    ),
    notes: preset?.notes || "",
  }));

  const done = status === "done";

  /* lock page scroll, trap focus, close on Escape, restore focus on exit */
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus({ preventScroll: true });

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const current = document.activeElement;
      if (e.shiftKey && (current === first || current === dialogRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [onClose]);

  const update = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((prev) => {
      const keys = Object.keys(patch).filter((k) => k in prev);
      if (keys.length === 0) return prev; // nothing to clear, skip a re-render
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  const toggleService = useCallback((label) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(label)
        ? f.services.filter((x) => x !== label)
        : [...f.services, label],
    }));
    setErrors((prev) => {
      if (!("services" in prev)) return prev;
      const next = { ...prev };
      delete next.services;
      return next;
    });
  }, []);

  const pickProperty = useCallback((v) => update({ property: v }), [update]);
  const pickTimeline = useCallback((v) => update({ timeline: v }), [update]);
  const pickMethod = useCallback((v) => update({ method: v }), [update]);

  const moveTo = (n) => {
    setDir(n > step ? 1 : -1);
    setStep(n);
    setErrors({});
    setSubmitError("");
    bodyRef.current?.scrollTo({ top: 0 });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;

    const found = validate(step, form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setTimeout(() => {
        bodyRef.current
          ?.querySelector('[data-error="true"]')
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 50);
      return;
    }

    if (step < STEPS.length - 1) {
      moveTo(step + 1);
      return;
    }

    setStatus("sending");
    setSubmitError("");
    try {
      await submitQuotation(form);
      setStatus("done");
    } catch {
      setStatus("idle");
      setSubmitError(
        "We could not send your request. Check your connection and try again."
      );
    }
  };

  /* ---------------------------- step contents ---------------------------- */

  const renderStep = () => {
    const isKenya = form.country === "Kenya";

    if (step === 0) {
      return (
        <div>
          <div
            role="group"
            aria-label="Services to quote"
            className="grid gap-3 sm:grid-cols-2"
          >
            {content.services.map((s) => (
              <Choice
                key={s.label}
                multi
                icon={s.Icon}
                label={s.label}
                hint={s.hint}
                value={s.label}
                selected={form.services.includes(s.label)}
                onPick={toggleService}
              />
            ))}
          </div>
          <ErrorText>{errors.services}</ErrorText>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="space-y-7">
          <div className="grid gap-5 sm:grid-cols-2">
            <Combobox
              id="quote-country"
              label="Country"
              value={form.country}
              options={COUNTRIES}
              placeholder="Search for your country"
              error={errors.country}
              // the county or region means something different in each country, so start fresh
              onChange={(c) =>
                update(
                  c === form.country
                    ? { country: c }
                    : { country: c, county: "", area: "" }
                )
              }
            />

            {isKenya ? (
              <Combobox
                id="quote-county"
                label="County"
                value={form.county}
                options={COUNTIES}
                placeholder="Type to search, e.g. Kiambu"
                error={errors.county}
                onChange={(c) => update({ county: c })}
              />
            ) : (
              <Field label="Region or district" error={errors.county}>
                <input
                  value={form.county}
                  onChange={(e) => update({ county: e.target.value })}
                  maxLength={60}
                  placeholder="e.g. Arusha, Kampala, Kigali"
                  autoComplete="address-level1"
                  className={inputClass(errors.county)}
                />
              </Field>
            )}

            <div className="sm:col-span-2">
              <Field
                label={isKenya ? "Town or area" : "Town or village"}
                optional={isKenya}
                error={errors.area}
              >
                <input
                  value={form.area}
                  onChange={(e) => update({ area: e.target.value })}
                  maxLength={100}
                  placeholder={
                    isKenya ? "e.g. Makongeni, Thika" : "e.g. the nearest town or village"
                  }
                  autoComplete="address-level2"
                  className={inputClass(errors.area)}
                />
              </Field>
            </div>
          </div>

          <Group legend="Type of property" error={errors.property}>
            <div
              role="radiogroup"
              aria-label="Type of property"
              className="grid grid-cols-2 gap-3 sm:grid-cols-4"
            >
              {content.property.map((p) => (
                <Choice
                  key={p.label}
                  compact
                  icon={p.Icon}
                  label={p.label}
                  value={p.label}
                  selected={form.property === p.label}
                  onPick={pickProperty}
                />
              ))}
            </div>
          </Group>

          <Group legend="When do you want to start?" error={errors.timeline}>
            <div
              role="radiogroup"
              aria-label="When do you want to start"
              className="flex flex-wrap gap-2.5"
            >
              {content.timeline.map((t) => (
                <Chip
                  key={t}
                  value={t}
                  selected={form.timeline === t}
                  onPick={pickTimeline}
                >
                  {t}
                </Chip>
              ))}
            </div>
          </Group>

          <Field label="Anything else we should know?" optional>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              maxLength={1000}
              placeholder="e.g. daily water needed, existing borehole or pump, roof type"
              className={inputClass()}
            />
          </Field>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* recap with quick edit links */}
        <div className="rounded-2xl bg-[#F3F7FB] p-4 sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500">Quote for</p>
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {form.services.map((s) => (
                  <li
                    key={s}
                    className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-[#0A3568] shadow-sm"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => moveTo(0)}
              className="shrink-0 text-sm font-bold text-[#1F7BE0] hover:underline"
            >
              Edit
            </button>
          </div>
          <div className="mt-4 flex items-start justify-between gap-4 border-t border-slate-200 pt-4">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-slate-500">Site</dt>
              <dd className="font-semibold text-[#0E1726]">
                {[form.area.trim(), form.county.trim(), form.country]
                  .filter(Boolean)
                  .join(", ")}
              </dd>
              <dt className="text-slate-500">Property</dt>
              <dd className="font-semibold text-[#0E1726]">{form.property}</dd>
              <dt className="text-slate-500">Start</dt>
              <dd className="font-semibold text-[#0E1726]">{form.timeline}</dd>
            </dl>
            <button
              type="button"
              onClick={() => moveTo(1)}
              className="shrink-0 text-sm font-bold text-[#1F7BE0] hover:underline"
            >
              Edit
            </button>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Your name" error={errors.name}>
            <input
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              maxLength={100}
              autoComplete="name"
              className={inputClass(errors.name)}
            />
          </Field>
          <Field label="Phone number" error={errors.phone}>
            <input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => update({ phone: e.target.value })}
              maxLength={40}
              autoComplete="tel"
              placeholder="07XX XXX XXX"
              className={inputClass(errors.phone)}
            />
          </Field>
        </div>

        <Group legend="Best way to reach you">
          <div
            role="radiogroup"
            aria-label="Best way to reach you"
            className="flex flex-wrap gap-2.5"
          >
            {METHODS.map((m) => (
              <Chip
                key={m.id}
                icon={m.icon}
                value={m.id}
                selected={form.method === m.id}
                onPick={pickMethod}
              >
                {m.label}
              </Chip>
            ))}
          </div>
        </Group>

        <Field
          label="Email"
          optional={form.method !== "email"}
          error={errors.email}
        >
          <input
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            maxLength={120}
            autoComplete="email"
            className={inputClass(errors.email)}
          />
        </Field>
      </div>
    );
  };

  /* ------------------------------- success ------------------------------- */

  const firstName = form.name.trim().split(" ")[0];
  const followUp = {
    call: "call you",
    whatsapp: "message you on WhatsApp",
    email: "email you",
  }[form.method];

  const successText = content.success.text
    .replaceAll("{name}", firstName)
    .replaceAll("{contact}", followUp);

  const renderDone = () => (
    <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center sm:px-10">
      <motion.div
        initial={{ scale: reduce ? 1 : 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
        className="flex h-20 w-20 items-center justify-center rounded-full bg-[#1F7BE0] text-white"
      >
        <Check size={38} strokeWidth={3} />
      </motion.div>
      <h2 className="font-heading mt-7 text-3xl font-extrabold text-[#0E1726]">
        {content.success.title}
      </h2>
      <p className="mt-3 max-w-md text-lg leading-relaxed text-slate-600">
        {successText}
      </p>
      <ul className="mt-6 flex max-w-md flex-wrap justify-center gap-1.5">
        {form.services.map((s) => (
          <li
            key={s}
            className="rounded-full bg-[#F3F7FB] px-3 py-1 text-sm font-semibold text-[#0A3568]"
          >
            {s}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onClose}
        className="mt-9 inline-flex items-center gap-2 rounded-xl bg-[#0A3568] px-8 py-4 font-bold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-[#1F7BE0] hover:shadow-xl"
      >
        Done
      </button>
    </div>
  );

  /* -------------------------------- render ------------------------------- */

  const last = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center md:items-center md:p-6">
      {/* no backdrop-blur: blurring a live page behind a modal is very slow */}
      <motion.div
        className="absolute inset-0 bg-[#031326]/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        aria-hidden="true"
      />

      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={content.panel.title}
        tabIndex={-1}
        initial={{ y: reduce ? 0 : 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: reduce ? 0 : 10, opacity: 0 }}
        transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative flex h-[94dvh] will-change-transform w-full max-w-5xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl outline-none md:h-[min(42rem,90vh)] md:flex-row md:rounded-[24px]"
      >
        {/* ------------------------- left panel (desktop) ------------------------- */}
        <aside className="hidden w-[300px] shrink-0 flex-col justify-between bg-gradient-to-b from-[#0A3568] to-[#072445] p-8 text-white md:flex">
          <div>
            <img
              src={LOGO}
              alt="MajiMaji Water Solutions"
              className="h-12 w-auto brightness-0 invert"
            />
            <h2 className="font-heading mt-8 text-3xl font-extrabold leading-tight">
              {content.panel.title}
            </h2>
            <p className="mt-2 text-white/70">{content.panel.text}</p>

            <ol className="mt-10">
              {STEPS.map((s, i) => {
                const complete = done || i < step;
                const current = !done && i === step;
                return (
                  <li key={i} className="relative flex gap-4">
                    {i < STEPS.length - 1 && (
                      <span
                        aria-hidden="true"
                        className={`absolute left-[15px] top-9 h-[calc(100%-2.25rem)] w-px transition-colors duration-500 ${
                          complete ? "bg-[#7DB8FF]" : "bg-white/20"
                        }`}
                      />
                    )}
                    <span
                      className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-300 ${
                        complete
                          ? "bg-[#7DB8FF] text-[#072445]"
                          : current
                          ? "bg-white text-[#0A3568]"
                          : "border border-white/30 text-white/60"
                      }`}
                    >
                      {complete ? <Check size={16} strokeWidth={3} /> : i + 1}
                    </span>
                    <div className="pb-9">
                      <p
                        className={`font-semibold ${
                          complete || current ? "text-white" : "text-white/60"
                        }`}
                      >
                        {s.label}
                      </p>
                      <p className="text-sm text-white/55">{s.short}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="flex items-start gap-3 text-sm leading-relaxed text-white/70">
            <ShieldCheck size={20} className="mt-0.5 shrink-0 text-[#7DB8FF]" />
            {content.panel.note}
          </div>
        </aside>

        {/* ------------------------------ right panel ----------------------------- */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close quote form"
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-[#0E1726] md:right-5 md:top-5"
          >
            <X size={22} />
          </button>

          {done ? (
            renderDone()
          ) : (
            <form
              onSubmit={onSubmit}
              noValidate
              className="flex min-h-0 flex-1 flex-col"
            >
              {/* header */}
              <div className="shrink-0 px-5 pb-5 pr-16 pt-6 sm:px-8 md:pt-8">
                <p className="text-sm font-bold text-[#1F7BE0] md:hidden">
                  Step {step + 1} of {STEPS.length}
                </p>
                <h2 className="font-heading text-2xl font-extrabold leading-tight text-[#0E1726] sm:text-[1.75rem]">
                  {STEPS[step].title}
                </h2>
                <p className="mt-1.5 text-slate-600">{STEPS[step].sub}</p>
                <div className="mt-4 h-1 overflow-hidden rounded-full bg-slate-100 md:hidden">
                  <div
                    className="h-full w-full origin-left rounded-full bg-[#1F7BE0] transition-transform duration-200 ease-out"
                    style={{ transform: `scaleX(${(step + 1) / STEPS.length})` }}
                  />
                </div>
              </div>

              {/* body */}
              <div
                ref={bodyRef}
                className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 sm:px-8"
              >
                <motion.div
                  key={step}
                  initial={
                    step === 0 && dir === 1
                      ? false
                      : { opacity: 0, x: reduce ? 0 : dir * 14 }
                  }
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: reduce ? 0 : 0.14, ease: "easeOut" }}
                >
                  {renderStep()}
                </motion.div>
              </div>

              {/* footer */}
              <div className="shrink-0 border-t border-slate-100 bg-white px-5 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pt-4 sm:px-8">
                {submitError && (
                  <p role="alert" className="mb-3 text-sm font-medium text-red-600">
                    {submitError}
                  </p>
                )}
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => moveTo(step - 1)}
                    disabled={status === "sending"}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-3.5 font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-[#0E1726] ${
                      step === 0 ? "invisible" : ""
                    }`}
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="group inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-xl bg-[#F2A93B] px-7 py-3.5 font-bold text-[#0B1F3A] transition duration-200 hover:-translate-y-0.5 hover:bg-[#0A3568] hover:text-white hover:shadow-xl disabled:pointer-events-none disabled:opacity-80"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending
                      </>
                    ) : last ? (
                      <>
                        <Send size={18} />
                        Send request
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight
                          size={18}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PROVIDER + HOOK                                                           */
/* -------------------------------------------------------------------------- */

const QuoteContext = createContext({ openQuote: () => {} });

export function useQuote() {
  return useContext(QuoteContext);
}

export function QuoteProvider({ children }) {
  const [state, setState] = useState({ open: false, preset: null, key: 0 });

  // Loaded once when the app starts, so the modal opens instantly with the
  // live wording. Until then (or if the read fails) the defaults are used.
  const [content, setContent] = useState(() => normalize(DEFAULT_QUOTE));

  useEffect(() => {
    let alive = true;
    withTimeout(loadQuote(), DB_TIMEOUT_MS)
      .then(({ data }) => {
        if (alive) setContent(normalize(data));
      })
      .catch(() => {
        /* keep the default content from quoteData.js */
      });
    return () => {
      alive = false;
    };
  }, []);

  const openQuote = useCallback((preset) => {
    // ignore click events if someone passes openQuote straight to onClick
    const safe =
      preset && typeof preset === "object" && !("nativeEvent" in preset)
        ? preset
        : null;
    setState((s) => ({ open: true, preset: safe, key: s.key + 1 }));
  }, []);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const value = useMemo(() => ({ openQuote }), [openQuote]);

  return (
    <QuoteContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {state.open && (
          <QuoteModal
            key={state.key}
            content={content}
            preset={state.preset}
            onClose={close}
          />
        )}
      </AnimatePresence>
    </QuoteContext.Provider>
  );
}

export default QuoteModal;